<template>
  <div class="shop-screen detail-screen">
    <header class="shop-header">
      <button
        class="back"
        @click="$router.back()"
      >
        <van-icon name="arrow-left" />
      </button>
      <h1>{{ $t('shop.product') }}</h1>
      <button
        v-if="isLoggedIn"
        class="favorite"
        :aria-label="favorited ? $t('shop.removeFavorite') : $t('shop.addFavorite')"
        :aria-pressed="favorited ? 'true' : 'false'"
        :disabled="favoriteSubmitting || !product"
        @click="toggleFavorite"
      >
        <van-icon :name="favorited ? 'like' : 'like-o'" />
      </button>
    </header>
    <main v-if="product">
      <section class="hero-image">
        <van-swipe
          v-if="carouselImages.length"
          :autoplay="3000"
          :loop="carouselImages.length > 1"
          class="product-swipe"
        >
          <van-swipe-item
            v-for="(image, index) in carouselImages"
            :key="image + index"
            class="hero-slide"
          >
            <img
              :src="image"
              :alt="product.title"
            >
          </van-swipe-item>
        </van-swipe>
        <img
          v-else
          :src="product.image"
          :alt="product.title"
        >
      </section>
      <section class="price-row">
        <strong>{{ money(product.price) }}</strong>
        <del v-if="product.market_price">{{ money(product.market_price) }}</del>
      </section>
      <section class="quantity">
        <h2>{{ $t('shop.quantity') }}</h2>
        <div class="stepper">
          <button
            :disabled="quantity <= 1"
            @click="change(-1)"
          >
            −
          </button>
          <span>{{ quantity }}</span>
          <button
            :disabled="quantity >= product.stock"
            @click="change(1)"
          >
            ＋
          </button>
        </div>
      </section>
      <section class="details">
        <h2>{{ $t('shop.productDetails') }}</h2>
        <div
          class="product-copy"
          v-html="product.content"
        />
      </section>
    </main>
    <footer class="detail-actions">
      <div class="total">
        {{ $t('shop.total') }}: <em>{{ money(total) }}</em><small>{{ quantity }} {{ $t('shop.parts') }}</small>
      </div>
      <button
        class="add"
        :disabled="submitting || !product || !product.stock"
        @click="addCart"
      >
        <van-icon name="add-o" />{{ $t('shop.addToCart') }}
      </button>
      <button
        class="buy"
        :disabled="submitting || !product || !product.stock"
        @click="buy"
      >
        {{ $t('shop.buy') }}
      </button>
    </footer>
  </div>
</template>

<script>
import Vue from 'vue';
import Fetch from '@/utils/fetch';
import { multiplyPen } from '@/utils/money';
import { Swipe, SwipeItem } from 'vant';

Vue.use(Swipe).use(SwipeItem);

export default {
    data() {
        return {
            product: null, quantity: 1, submitting: false, favorited: false, favoriteSubmitting: false
        };
    },
    computed: {
        total() {
            return this.product ? multiplyPen(this.product.price, this.quantity) : '0';
        },
        carouselImages() {
            return this.product && Array.isArray(this.product.carousel_images) ? this.product.carousel_images : [];
        },
        isLoggedIn() {
            return !!localStorage.getItem('token');
        }
    },
    mounted() {
        this.$parent.footer('item', false);
        Fetch('/shop/product', { id: this.$route.params.id }, null, true).then((res) => {
            this.product = res.data;
            if (this.isLoggedIn) {
                this.loadFavoriteStatus();
            }
        });
    },
    methods: {
        money(value) {
            return this.$formatPen(value);
        },
        change(amount) {
            this.quantity = Math.min(this.product.stock, Math.max(1, this.quantity + amount));
        },
        addCart() {
            this.submit('/shop/cartAdd', { product_id: this.product.id, quantity: this.quantity }, () => {
                this.$toast(this.$t('shop.addedToCart'));
            });
        },
        buy() {
            this.submit('/shop/orderDirect', {
                product_id: this.product.id,
                quantity: this.quantity,
                request_id: this.requestId()
            }, (data) => {
                this.$router.replace('/shopOrder/' + data.id);
            });
        },
        loadFavoriteStatus() {
            if (!this.isLoggedIn || !this.product) return;
            Fetch('/shop/favoriteStatus', { product_id: this.product.id }).then((res) => {
                this.favorited = !!(res.data && res.data.favorited);
            }).catch(() => {});
        },
        toggleFavorite() {
            if (this.favoriteSubmitting || !this.product) return;
            const next = !this.favorited;
            this.favoriteSubmitting = true;
            Fetch('/shop/favoriteSet', {
                product_id: this.product.id,
                favorite: next ? 1 : 0
            }, null, true).then((res) => {
                this.favorited = !!(res.data && res.data.favorited);
                this.$toast(this.$t(this.favorited ? 'shop.favoriteAdded' : 'shop.favoriteRemoved'));
            }).finally(() => {
                this.favoriteSubmitting = false;
            });
        },
        submit(url, data, callback) {
            if (this.submitting) return;
            this.submitting = true;
            Fetch(url, data, null, true).then((res) => callback(res.data)).finally(() => {
                this.submitting = false;
            });
        },
        requestId() {
            return 'shop_' + Date.now() + '_' + Math.random().toString(36).slice(2, 12);
        }
    }
};
</script>

<style scoped>
.shop-screen { min-height: 100vh; box-sizing: border-box; padding-bottom: calc(82px + env(safe-area-inset-bottom, 0px)); background: #f5f5f5; color: #333; }
.shop-header { position: relative; display: flex; height: 50px; align-items: center; justify-content: center; border-bottom: 1px solid #edf0f2; background: #fff; }
.shop-header h1 { margin: 0; color: #333; font-size: 16px; font-weight: 500; }
.back { position: absolute; left: 16px; display: inline-flex; width: 36px; height: 36px; align-items: center; justify-content: flex-start; padding: 0; border: 0; background: transparent; color: #ee4d2d; font-size: 20px; }
.favorite { position: absolute; right: 16px; display: inline-flex; width: 36px; height: 36px; align-items: center; justify-content: flex-end; padding: 0; border: 0; background: transparent; color: #ee4d2d; font-size: 22px; }
.favorite:disabled { opacity: .5; }
.hero-image { display: flex; height: 70vw; min-height: 300px; max-height: 404px; justify-content: center; align-items: center; background: #fff; }
.hero-image img { display: block; width: 82%; max-width: 404px; height: 100%; object-fit: contain; background: #fff; }
.product-swipe { width: 100%; height: 100%; }
.hero-slide { display: flex; height: 100%; align-items: center; justify-content: center; }
.price-row { display: flex; min-height: 64px; align-items: center; gap: 7px; margin-bottom: 10px; padding: 0 16px; background: #fff; }
.price-row strong { color: #ee4d2d; font-size: 24px; font-weight: 500; letter-spacing: -.3px; }
.price-row del { color: #929aa3; font-size: 16px; }
.quantity, .details { margin-top: 10px; padding: 16px; background: #fff; }
h2 { margin: 0 0 12px; color: #333; font-size: 16px; font-weight: 500; line-height: 22px; }
.stepper { display: flex; align-items: center; height: 38px; }
.stepper button, .stepper span { display: inline-flex; width: 42px; height: 38px; align-items: center; justify-content: center; border: 0; font-size: 17px; }
.stepper button { background: #f5f5f5; color: #555; }
.stepper button:disabled { color: #c7ccd3; }
.stepper span { background: #fff; border-top: 1px solid #eee; border-bottom: 1px solid #eee; color: #333; font-size: 16px; }
.details { min-height: 300px; padding-bottom: 28px; }
.details h2 { margin-bottom: 14px; border-left: 3px solid #ee4d2d; padding-left: 9px; }
.product-copy { color: #555; font-size: 14px; font-weight: 400; line-height: 1.55; word-break: break-word; }
.detail-actions { position: fixed; z-index: 10; right: 0; bottom: 0; left: 0; display: flex; min-height: 72px; box-sizing: border-box; align-items: center; gap: 10px; padding: 9px 16px calc(9px + env(safe-area-inset-bottom, 0px)); background: #fff; box-shadow: 0 -1px 4px rgba(0, 0, 0, .08); }
.total { flex: 1; color: #31343a; font-size: 14px; line-height: 19px; }
.total em { color: #ee4d2d; font-style: normal; }
.total small { display: block; font-size: 12px; }
.detail-actions button { display: inline-flex; min-width: 74px; min-height: 42px; align-items: center; justify-content: center; gap: 4px; border: 1px solid #ee4d2d; border-radius: 4px; color: #ee4d2d; font-size: 14px; }
.detail-actions .add { min-width: 128px; background: #fff; }
.detail-actions .buy { background: #ee4d2d; color: #fff; }
.detail-actions button:disabled { opacity: .55; }
</style>
